import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtRupiah, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { MATERIAL_USAGE_VIEW, MATERIAL_USAGE_RECORD, MATERIAL_USAGE_MONEY, MATERIAL_SOURCES, MATERIAL_CATEGORIES } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, PlusCircle, Search, Trash2, PackageOpen } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { project_uuid: "", material_name: "", category: "Lainnya", qty: "", unit: "unit", price: "", used_date: today(), source: "Warehouse", doc_no: "", note: "" };

export default function MaterialUsage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const canView = user && MATERIAL_USAGE_VIEW.includes(user.role);
  const canRecord = user && MATERIAL_USAGE_RECORD.includes(user.role);
  const canMoney = user && MATERIAL_USAGE_MONEY.includes(user.role);

  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const projectFilter = params.get("project") || "";

  const load = useCallback(() => {
    setLoading(true);
    const p = projectFilter ? { project_uuid: projectFilter } : {};
    api.get("/material-usage", { params: p }).then(({ data }) => setList(data)).finally(() => setLoading(false));
  }, [projectFilter]);

  useEffect(() => { if (canView) load(); }, [load, canView]);
  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(Array.isArray(data) ? data : (data.items || []))).catch(() => {}); }, []);

  const openNew = () => { setForm({ ...emptyForm, project_uuid: projectFilter || "" }); setOpen(true); };

  const save = async () => {
    if (!form.project_uuid) return toast.error("Pilih proyek dulu");
    if (!form.material_name.trim()) return toast.error("Nama material wajib diisi");
    setSaving(true);
    try {
      await api.post("/material-usage", {
        project_uuid: form.project_uuid, material_name: form.material_name.trim(), category: form.category,
        qty: Number(form.qty) || 0, unit: form.unit || "unit", price: Number(form.price) || 0,
        used_date: form.used_date || today(), source: form.source, doc_no: form.doc_no, note: form.note,
      });
      toast.success("Pemakaian bahan dicatat");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Arsipkan catatan pemakaian ini?")) return;
    try { await api.delete(`/material-usage/${id}`); toast.success("Catatan diarsipkan"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!canView) return <Card className="p-10 text-center text-muted-foreground" data-testid="access-denied">Akses Ditolak.</Card>;

  const filtered = q ? list.filter((m) => (`${m.material_name} ${m.project_id} ${m.plate} ${m.customer_name}`).toLowerCase().includes(q.toLowerCase())) : list;
  const totalHpp = filtered.reduce((s, m) => s + (Number(m.total) || 0), 0);

  return (
    <div className="space-y-4" data-testid="material-usage-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Material Usage</h1>
          <p className="text-sm text-muted-foreground">Material Sheet — pemakaian bahan aktual (sumber HPP)</p>
        </div>
        {canRecord && <Button onClick={openNew} data-testid="new-usage-btn" className="bg-primary hover:bg-red-700"><PlusCircle size={16} className="mr-1.5" /> Catat Pemakaian</Button>}
      </div>

      <Card className="p-3 flex flex-col sm:flex-row gap-2 items-center">
        <div className="relative flex-1 w-full">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari material, proyek, no. polisi..." className="pl-9" data-testid="usage-search" />
        </div>
        {canMoney && <div className="text-sm whitespace-nowrap">Total HPP (tampil): <b className="text-navy" data-testid="usage-total">{fmtRupiah(totalHpp)}</b></div>}
      </Card>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        filtered.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground" data-testid="usage-empty"><PackageOpen className="mx-auto mb-2 opacity-40" /> Belum ada pemakaian bahan dicatat.</Card>
        ) : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100"><tr className="text-left text-xs">
                <th className="p-2.5">Tanggal</th><th className="p-2.5">Proyek</th><th className="p-2.5">Material</th>
                <th className="p-2.5 text-right">Qty</th><th className="p-2.5">Satuan</th>
                {canMoney && <th className="p-2.5 text-right">Harga</th>}{canMoney && <th className="p-2.5 text-right">Total</th>}
                <th className="p-2.5">Sumber</th><th className="p-2.5">PIC</th>{canRecord && <th></th>}
              </tr></thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} className="border-t hover:bg-slate-50" data-testid={`usage-row-${m.id}`}>
                    <td className="p-2.5 whitespace-nowrap">{fmtDate(m.used_date)}</td>
                    <td className="p-2.5"><div className="font-mono-k3 text-xs text-primary">{m.project_id}</div><div className="text-xs text-muted-foreground">{m.plate}</div></td>
                    <td className="p-2.5"><div className="font-medium">{m.material_name}</div><div className="text-xs text-muted-foreground">{m.category}</div></td>
                    <td className="p-2.5 text-right">{m.qty}</td>
                    <td className="p-2.5">{m.unit}</td>
                    {canMoney && <td className="p-2.5 text-right">{fmtRupiah(m.price)}</td>}
                    {canMoney && <td className="p-2.5 text-right font-medium">{fmtRupiah(m.total)}</td>}
                    <td className="p-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100">{m.source}</span></td>
                    <td className="p-2.5 text-xs text-muted-foreground">{m.pic}</td>
                    {canRecord && <td className="p-2.5"><button onClick={() => remove(m.id)} data-testid={`usage-del-${m.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="usage-dialog">
          <DialogHeader><DialogTitle>Catat Pemakaian Bahan</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label>Proyek / Kendaraan *</Label>
              <Select value={form.project_uuid} onValueChange={(v) => setForm((f) => ({ ...f, project_uuid: v }))}>
                <SelectTrigger data-testid="usage-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_id} · {p.plate || "-"} · {p.customer_name || ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Nama Material *</Label>
              <Input value={form.material_name} onChange={(e) => setForm((f) => ({ ...f, material_name: e.target.value }))} placeholder="mis. Plat 1.2mm, Cat, Thinner" data-testid="usage-material" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="usage-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{MATERIAL_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label>Sumber</Label>
                <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                  <SelectTrigger data-testid="usage-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{MATERIAL_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-1.5"><Label>Qty</Label><Input type="number" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} data-testid="usage-qty" /></div>
              <div className="grid gap-1.5"><Label>Satuan</Label><Input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} data-testid="usage-unit" /></div>
              {canMoney && <div className="grid gap-1.5"><Label>Harga Aktual</Label><Input type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid="usage-price" /></div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Tanggal Pakai</Label><Input type="date" value={form.used_date} onChange={(e) => setForm((f) => ({ ...f, used_date: e.target.value }))} data-testid="usage-date" /></div>
              <div className="grid gap-1.5"><Label>No. Dokumen</Label><Input value={form.doc_no} onChange={(e) => setForm((f) => ({ ...f, doc_no: e.target.value }))} placeholder="opsional" data-testid="usage-doc" /></div>
            </div>
            <div className="grid gap-1.5"><Label>Catatan</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="opsional" data-testid="usage-note" /></div>
            {!canMoney && <p className="text-xs text-muted-foreground">Harga disembunyikan untuk peran Anda. Qty tetap tercatat; harga diisi oleh Admin/Finance.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-red-700" data-testid="usage-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
